import { FormGroup } from '@angular/forms';

import { KeyValueType } from '@models/common';
import {
  DisplayType,
  FieldOrigin,
  FieldTypeEnum,
  FormattingType,
  KeyValueFormattingModel,
  KeyValueRestrictionModel,
  RestrictionType,
} from '@models/module/fields/enums';
import { FieldModel } from '@models/module/fields/main';
import {
  CipoCheckboxControl,
  CipoControl,
  CipoDateTimePickerType,
  CipoListControl,
  CipoTextControl,
} from '@shared/components/fields/common';

import {
  ALL_MIME_TYPES_KEY,
  DisplayTypeDefinition,
  FORMATTINGS,
  FieldTypeDefinition,
  FormattingName,
  RESTRICTIONS,
  RestrictionName,
} from '../common';

export interface FieldConfigDialogData {
  moduleId: number;
  moduleTypeId: number;
  entityId: number;
  canAddToForm: boolean;
  fieldId?: number;
  fieldOrigin?: FieldOrigin;
  screenId?: number;
  newField?: { typeId: FieldTypeEnum; displayTypeId: DisplayType };
  addToForm?: boolean;
  tableId?: number;
}

export interface FieldConfigDialogResult {
  field: Partial<FieldModel>;
  screenId: number;
  addToForm: boolean;
}

export interface RestrictionState {
  name: RestrictionName;
  key: RestrictionType;
  value?: boolean | number;
  restrictionValue?: number;
  mimeTypes?: number[];
}

export interface FormattingState {
  name: FormattingName;
  key: FormattingType;
  value?: boolean | number;
  formattingValue?: number;
}

export interface FieldConfigFormGroup {
  name: CipoTextControl;
  label: CipoTextControl;
  displayTypeId: CipoListControl;
  hasMultipleValues: CipoCheckboxControl;
  aggregateId: CipoListControl;
  dataSourceId: CipoListControl;
  weatherDateId: CipoListControl;
  weatherLocationId: CipoListControl;
  currentDateTime: CipoCheckboxControl;
  isFilter: CipoCheckboxControl;
  defaultValue: CipoControl<unknown>;
  restrictions: FormGroup<Record<string, CipoControl<unknown>>>;
  formattings: FormGroup<Record<string, CipoControl<unknown>>>;
}

export const DATE_PICKER_TYPES: Record<number, CipoDateTimePickerType> = {
  3: 'date',
  4: 'dateTime',
  5: 'time',
};

export const INTEGER_RESTRICTION_VALUE = 2;
export const ADDRESSES_DATA_SOURCE_ID = -1019;
export const DEFAULT_FIELD_VERSION: KeyValueType<number, string> = {
  key: 0,
  value: 'modules.fieldConfig.defaultVersion',
};

const isPresent = (value: unknown) => value !== undefined && value !== null;

export const initRestrictions = (
  type: FieldTypeDefinition,
  stored: KeyValueRestrictionModel[] = [],
  isExisting = false,
): RestrictionState[] => {
  const storedByKey = new Map(stored.map(restriction => [restriction.key, restriction]));

  return type.restrictions.map((name, index) => {
    const definition = RESTRICTIONS[name];
    const saved = storedByKey.get(definition.key);
    const state: RestrictionState = { name, key: definition.key };

    if (definition.isMimeTypes) {
      state.mimeTypes = saved?.mimeTypes?.length ? [...saved.mimeTypes] : [ALL_MIME_TYPES_KEY];
      return state;
    }
    if (definition.isNumberInput) {
      state.restrictionValue = saved?.restrictionValue;
      return state;
    }

    let mapped: boolean | number = definition.defaultValue;
    if (saved) {
      mapped =
        isPresent(saved.value) && (saved.value as number) !== 0
          ? saved.value
          : definition.elements.length
            ? saved.value
            : true;
    }

    let value: boolean | number = definition.elements.length ? definition.elements[0].key : definition.defaultValue;
    if (index < type.defaultRestrictions.length) {
      value = mapped !== 0 && mapped !== false ? mapped : (type.defaultRestrictions[index] as boolean | number);
    }
    if (isExisting && definition.defaultValue === true && !saved) {
      value = false;
    }
    state.value = value;
    return state;
  });
};

export const initFormattings = (
  display: DisplayTypeDefinition,
  stored: KeyValueFormattingModel[] = [],
): FormattingState[] => {
  const storedByKey = new Map(stored.map(formatting => [formatting.key, formatting]));

  return display.formattings.map((name, index) => {
    const definition = FORMATTINGS[name];
    const saved = storedByKey.get(definition.key);
    const mapped = saved?.value ? (saved.value as number) : definition.defaultValue;

    let value: number = definition.elements.length ? definition.elements[0].key : undefined;
    if (index < display.defaultFormattingIds.length) {
      value = definition.elements.length ? (mapped ?? display.defaultFormattingIds[index]) : undefined;
    }
    return {
      name,
      key: definition.key,
      value,
      formattingValue: saved ? saved.formattingValue : definition.defaultFormattingValue,
    };
  });
};

export const requiredRestrictionMissing = (restrictions: RestrictionState[]): boolean =>
  restrictions.some(state => RESTRICTIONS[state.name].required && !state.value);

export const serializeRestrictions = (
  restrictions: RestrictionState[],
  typeId: FieldTypeEnum,
): KeyValueRestrictionModel[] =>
  restrictions.flatMap(state => {
    if (isPresent(state.restrictionValue)) {
      return [{ key: state.key, restrictionValue: state.restrictionValue } as KeyValueRestrictionModel];
    }
    if (state.value === false) {
      return [];
    }
    const entry = { key: state.key } as KeyValueRestrictionModel;
    if (state.value !== undefined) {
      entry.value = (state.value === true ? null : state.value) as number;
    }
    if (typeId === FieldTypeEnum.Attachment && state.mimeTypes) {
      entry.mimeTypes = state.mimeTypes.includes(ALL_MIME_TYPES_KEY) ? [] : state.mimeTypes;
    }
    return [entry];
  });

export const serializeFormattings = (formattings: FormattingState[]): KeyValueFormattingModel[] =>
  formattings
    .filter(state => state.value !== false)
    .map(
      state =>
        ({
          key: state.key,
          value: state.value === true ? null : state.value,
          formattingValue: state.formattingValue,
        }) as KeyValueFormattingModel,
    );

export const restrictionValue = (restrictions: RestrictionState[], name: RestrictionName) =>
  restrictions.find(state => state.name === name)?.value;
